import { useState } from 'react';
import type { GameHistoryEntry } from '../api.ts';
import { Card } from './ui.tsx';

export function GameHistory({
  games,
  title,
  collapsible = false,
}: {
  games: GameHistoryEntry[];
  title: string;
  collapsible?: boolean;
}) {
  const [open, setOpen] = useState<string | null>(null);
  const [archiveOpen, setArchiveOpen] = useState(!collapsible);
  if (games.length === 0) return null;
  return (
    <section className="mt-6">
      {collapsible ? (
        <button
          type="button"
          className="mb-3 flex w-full items-center justify-between gap-3 text-left font-medium"
          aria-expanded={archiveOpen}
          onClick={() => setArchiveOpen((expanded) => !expanded)}
        >
          <span>{title} ({games.length})</span>
          <span aria-hidden className="text-text-faint">{archiveOpen ? '−' : '+'}</span>
        </button>
      ) : (
        <h2 className="mb-3 font-medium">{title}</h2>
      )}
      {archiveOpen && <div className="flex flex-col gap-3">
        {games.map((game) => {
          const expanded = open === game.id;
          const entries = new Map(game.entries.map((entry) => [entry.id, entry]));
          const names = new Map(game.people.map((person) => [person.id, person.name]));
          const champion = game.championEntryId ? entries.get(game.championEntryId) : null;
          return (
            <Card key={game.id}>
              <button type="button" className="flex w-full items-start justify-between gap-3 text-left" onClick={() => setOpen(expanded ? null : game.id)} aria-expanded={expanded}>
                <span>
                  <strong className="block">
                    {game.name}
                    {game.isSolo && (
                      <span className="ml-2 rounded bg-surface-raised px-1.5 py-0.5 align-middle text-xs font-normal text-text-faint">
                        solo · not counted
                      </span>
                    )}
                  </strong>
                  <span className="text-xs text-text-faint">{new Date(game.endedAt).toLocaleString()} · {game.people.map((p) => p.name).join(', ')}</span>
                  {champion && <span className="mt-1 block text-sm text-accent">Winner: {champion.title} — {names.get(champion.userId)}</span>}
                </span>
                <span className="text-text-faint">{expanded ? '−' : '+'}</span>
              </button>
              {expanded && (
                <div className="mt-4 border-t border-border pt-4">
                  <h3 className="mb-2 text-sm font-medium">Suggestions</h3>
                  <ul className="mb-4 grid gap-1 text-sm sm:grid-cols-2">
                    {game.entries.map((entry) => <li key={entry.id}><a className="text-accent hover:underline" href={entry.sourceUrl} target="_blank" rel="noreferrer">{entry.title}</a>{entry.artist ? ` — ${entry.artist}` : ''} <span className="text-text-faint">({names.get(entry.userId)})</span></li>)}
                  </ul>
                  <h3 className="mb-2 text-sm font-medium">Bracket, votes and decisions</h3>
                  <ol className="flex flex-col gap-2 text-sm">
                    {game.matches.filter((m) => m.a || m.b).map((match) => {
                      const a = match.a ? entries.get(match.a) : null;
                      const b = match.b ? entries.get(match.b) : null;
                      const counts = new Map<string, number>();
                      for (const vote of match.votes) counts.set(vote.entryId, (counts.get(vote.entryId) ?? 0) + 1);
                      const votersFor = (entryId: string | undefined) => match.votes.filter((vote) => vote.entryId === entryId).map((vote) => `${names.get(vote.userId) ?? 'someone'}${vote.votingRound > 1 ? ' (tiebreak)' : ''}`).join(', ') || 'none';
                      return <li key={`${match.kind}:${match.round}:${match.index}`} className="rounded-lg bg-surface-raised p-2"><span className="text-text-faint">Round {match.round}{match.kind !== 'normal' ? ` · ${match.kind}` : ''}</span><br />{a?.title ?? 'Bye'} ({a ? counts.get(a.id) ?? 0 : 0}) vs {b?.title ?? 'Bye'} ({b ? counts.get(b.id) ?? 0 : 0})<br /><span className="text-xs text-text-faint">Winner: {match.winner ? entries.get(match.winner)?.title : '—'} · {match.decidedBy?.replaceAll('_', ' ') ?? 'not decided'}{match.votes.some((v) => v.votingRound > 1) ? ' · tiebreak vote used' : ''}</span>{match.votes.length > 0 && <span className="mt-1 block text-xs text-text-faint">{a?.title}: {votersFor(a?.id)} · {b?.title}: {votersFor(b?.id)}</span>}</li>;
                    })}
                  </ol>
                </div>
              )}
            </Card>
          );
        })}
      </div>}
    </section>
  );
}
